import { toast } from 'react-toastify';

/**
 * Error Handler Utilities
 * Centralized error handling for API and form errors
 */

/**
 * Error types
 */
export const ErrorType = {
  VALIDATION: 'validation',
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  NETWORK: 'network',
  SERVER: 'server',
  NOT_FOUND: 'not_found',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown',
};

/**
 * Check if error is a network error
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  if (!error) return false;

  // Check for fetch/axios network errors
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return true;
  }

  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
    return true;
  }

  if (error.message && (
    error.message.toLowerCase().includes('network') ||
    error.message.toLowerCase().includes('connection') ||
    error.message.toLowerCase().includes('timeout')
  )) {
    return true;
  }

  return false;
};

/**
 * Check if error is an authentication error
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const isAuthenticationError = (error) => {
  if (!error) return false;

  const status = error.status || error.response?.status;
  if (status === 401) return true;

  const message = (error.message || error.response?.data?.message || '').toLowerCase();
  const authKeywords = [
    'unauthorized',
    'unauthenticated',
    'authentication failed',
    'invalid token',
    'token expired',
    'session expired',
    'jwt expired',
  ];

  return authKeywords.some(keyword => message.includes(keyword));
};

/**
 * Check if error is an authorization error
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const isAuthorizationError = (error) => {
  if (!error) return false;

  const status = error.status || error.response?.status;
  if (status === 403) return true;

  const message = (error.message || error.response?.data?.message || '').toLowerCase();
  const authzKeywords = [
    'forbidden',
    'access denied',
    'insufficient permissions',
    'not authorized',
  ];

  return authzKeywords.some(keyword => message.includes(keyword));
};

/**
 * Check if error is a validation error
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const isValidationError = (error) => {
  if (!error) return false;

  const status = error.status || error.response?.status;
  if (status === 400 || status === 422) return true;

  return error.type === 'validation' || error.name === 'ValidationError';
};

/**
 * Check if error is a not found error
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const isNotFoundError = (error) => {
  if (!error) return false;

  const status = error.status || error.response?.status;
  return status === 404;
};

/**
 * Check if error is a server error
 * @param {Error|Object} error - Error object
 * @returns {boolean}
 */
export const isServerError = (error) => {
  if (!error) return false;

  const status = error.status || error.response?.status;
  return status >= 500 && status < 600;
};

/**
 * Determine error type
 * @param {Error|Object} error - Error object
 * @returns {string} Error type from ErrorType enum
 */
export const getErrorType = (error) => {
  if (isNetworkError(error)) return ErrorType.NETWORK;
  if (isAuthenticationError(error)) return ErrorType.AUTHENTICATION;
  if (isAuthorizationError(error)) return ErrorType.AUTHORIZATION;
  if (isValidationError(error)) return ErrorType.VALIDATION;
  if (isNotFoundError(error)) return ErrorType.NOT_FOUND;
  if (isServerError(error)) return ErrorType.SERVER;

  return ErrorType.UNKNOWN;
};

/**
 * Extract error message from error object
 * @param {Error|Object} error - Error object
 * @returns {string} User-friendly error message
 */
export const extractErrorMessage = (error) => {
  if (!error) return 'An unexpected error occurred';

  // Check for various message locations
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.response?.data?.error) return error.response.data.error;
  if (error.response?.statusText) return error.response.statusText;

  return 'An unexpected error occurred';
};

/**
 * Format error message for display
 * @param {Error|Object} error - Error object
 * @returns {string} Formatted user-friendly error message
 */
export const formatErrorMessage = (error) => {
  const errorType = getErrorType(error);
  const message = extractErrorMessage(error);

  switch (errorType) {
    case ErrorType.NETWORK:
      return 'Unable to connect to the server. Please check your internet connection and try again.';

    case ErrorType.AUTHENTICATION:
      return 'Your session has expired. Please sign in again to continue.';

    case ErrorType.AUTHORIZATION:
      return 'You do not have permission to perform this action.';

    case ErrorType.VALIDATION:
      return message || 'Please check your input and try again.';

    case ErrorType.NOT_FOUND:
      return 'The requested resource was not found.';

    case ErrorType.SERVER:
      return 'A server error occurred. Please try again later or contact support if the problem persists.';

    case ErrorType.TIMEOUT:
      return 'The request timed out. Please try again.';

    default:
      return message || 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Handle API error and show appropriate toast notification
 * @param {Error|Object} error - Error object
 * @param {Object} options - Additional options
 * @param {string} options.fallbackMessage - Fallback message if error message cannot be extracted
 * @param {boolean} options.showToast - Whether to show toast notification (default: true)
 * @param {string} options.toastId - Custom toast ID to prevent duplicates
 * @returns {Object} Processed error object with type and message
 */
export const handleApiError = (error, options = {}) => {
  const {
    fallbackMessage,
    showToast = true,
    toastId,
  } = options;

  const errorType = getErrorType(error);
  const errorMessage = formatErrorMessage(error);
  const finalMessage = errorMessage || fallbackMessage || 'An error occurred';

  // Show toast notification
  if (showToast) {
    showErrorToast(error, { message: finalMessage, toastId });
  }

  // Log error to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('API Error:', {
      type: errorType,
      message: finalMessage,
      originalError: error,
    });
  }

  return {
    type: errorType,
    message: finalMessage,
    originalError: error,
  };
};

/**
 * Show error toast notification
 * @param {Error|Object} error - Error object
 * @param {Object} options - Toast options
 * @param {string} options.message - Custom error message
 * @param {string} options.toastId - Custom toast ID
 * @param {number} options.autoClose - Auto close duration (default: 5000)
 */
export const showErrorToast = (error, options = {}) => {
  const {
    message,
    toastId,
    autoClose = 5000,
  } = options;

  const errorType = getErrorType(error);
  const errorMessage = message || formatErrorMessage(error);

  // Don't show duplicate toasts for authentication errors
  if (errorType === ErrorType.AUTHENTICATION && toastId === 'auth-error') {
    return;
  }

  const toastOptions = {
    position: 'top-right',
    autoClose,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  };

  if (toastId) {
    toastOptions.toastId = toastId;
  }

  toast.error(errorMessage, toastOptions);
};

/**
 * Show success toast notification
 * @param {string} message - Success message
 * @param {Object} options - Toast options
 */
export const showSuccessToast = (message, options = {}) => {
  toast.success(message, {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    ...options,
  });
};

/**
 * Show warning toast notification
 * @param {string} message - Warning message
 * @param {Object} options - Toast options
 */
export const showWarningToast = (message, options = {}) => {
  toast.warning(message, {
    position: 'top-right',
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    ...options,
  });
};

/**
 * Show info toast notification
 * @param {string} message - Info message
 * @param {Object} options - Toast options
 */
export const showInfoToast = (message, options = {}) => {
  toast.info(message, {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    ...options,
  });
};

/**
 * Parse validation errors from API response
 * @param {Object} error - Error object from API
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const parseValidationErrors = (error) => {
  const errors = {};

  if (!error) return errors;

  // Check for validation errors in various formats
  const validationErrors =
    error.response?.data?.errors ||
    error.response?.data?.validationErrors ||
    error.errors ||
    error.validationErrors;

  if (validationErrors) {
    // Handle array format
    if (Array.isArray(validationErrors)) {
      validationErrors.forEach((err) => {
        if (err.field || err.path) {
          const field = err.field || err.path;
          errors[field] = err.message || 'Invalid value';
        }
      });
    }
    // Handle object format
    else if (typeof validationErrors === 'object') {
      Object.keys(validationErrors).forEach((field) => {
        errors[field] = validationErrors[field];
      });
    }
  }

  return errors;
};

/**
 * Create error object for form submission
 * @param {string} message - Error message
 * @param {Object} fieldErrors - Field-specific errors
 * @returns {Object} Error object
 */
export const createFormError = (message, fieldErrors = {}) => {
  return {
    type: ErrorType.VALIDATION,
    message,
    fieldErrors,
  };
};

/**
 * Log error to error tracking service (placeholder for future implementation)
 * @param {Error|Object} error - Error object
 * @param {Object} context - Additional context
 */
export const logErrorToService = (error, context = {}) => {
  // Placeholder for integration with error tracking services like Sentry, LogRocket, etc.
  if (process.env.NODE_ENV === 'development') {
    console.error('Error logged:', error, context);
  }

  // TODO: Implement actual error tracking integration
  // Example: Sentry.captureException(error, { extra: context });
};

export default {
  ErrorType,
  isNetworkError,
  isAuthenticationError,
  isAuthorizationError,
  isValidationError,
  isNotFoundError,
  isServerError,
  getErrorType,
  extractErrorMessage,
  formatErrorMessage,
  handleApiError,
  showErrorToast,
  showSuccessToast,
  showWarningToast,
  showInfoToast,
  parseValidationErrors,
  createFormError,
  logErrorToService,
};